import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { DistributionRow } from '@/services/dashboard.service';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '@/utils/helpers';

interface StratificationsTableProps {
  rows: DistributionRow[];
  total?: DistributionRow;
  title?: string;
  firstColumnHeader?: string;
}

function StratificationsTable({
  rows,
  total,
  title,
  firstColumnHeader,
}: StratificationsTableProps) {
  const displayRows = [...rows, total];

  return (
    <>
      <Separator />

      <h3
        className=" font-semibold text-gray-900 mb-4"
        data-test-id="interest-rate-type-title"
      >
        {title}
      </h3>
      <div className="overflow-x-auto border border-gray-300 rounded-lg">
        <table
          className="relative table-responsive min-w-full divide-y divide-gray-200 overflow-hidden"
          data-test-id="stratifications-table"
        >
          <thead
            className="bg-gray-50 sticky top-0 z-10 py-3"
            data-test-id="strats-header"
          >
            <tr data-test-id="strats-header-row">
              <th className="text-center py-3  text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                {firstColumnHeader}
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                Loan Count
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                Outstanding Balances
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                % of Portfolio
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                NPV
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                WA Price
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                WA Benchmark Yield
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                WA Credit Spread
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                WA Market Yield
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                WAL
              </th>
              <th className="text-center py-3 text-xs font-medium text-gray-500 tracking-wider border-r border-gray-300">
                Modified Duration
              </th>
            </tr>
          </thead>

          <tbody
            className="bg-white divide-y divide-gray-200"
            data-test-id="strats-body"
          >
            {rows.length === 0 ? (
              <tr>
                <td colSpan={11} className="table-cell text-center py-8">
                  <div className="flex items-center justify-center gap-3">
                    <span>No sector data</span>
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={11}
                  className="table-cell text-center py-8 text-gray-500"
                >
                  No sector data
                </td>
              </tr>
            ) : (
              displayRows.map((r) => (
                <tr
                  className={cn(
                    'hover:bg-gray-200',
                    r?.category === 'Total' ? 'bg-gray-100 ' : ''
                  )}
                  key={r?.category}
                >
                  <td className="table-cell border-r border-gray-300 font-medium text-left">
                    {r?.category}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {formatNumber(r?.loan_count, 0)}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {formatCurrency(r?.outstanding_balance)}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {formatPercentage(r?.pct_of_portfolio)}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {r?.total_npv ? formatCurrency(r.total_npv) : '—'}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {r?.wa_price ? formatPercentage(Number(r.wa_price) * 100, 3) : '—'}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {formatPercentage(Number(r?.wa_benchmark_yield) * 100, 3)}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {formatPercentage(Number(r?.wa_credit_spread) * 100, 3)}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {formatPercentage(Number(r?.wa_market_yield) * 100, 3)}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {formatNumber(r?.wal, 2)}
                  </td>
                  <td className="table-cell border-r border-gray-300 font-medium text-right">
                    {formatNumber(r?.modified_duration, 2)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default StratificationsTable;
